<script lang="ts">
  import { session, afterDuelRoute, loginRoute } from "./lib/stores/session.svelte.ts";
  import { listen, getSocket } from "./lib/stores/net.svelte.ts";
  import { beginDuel, duel } from "./lib/stores/duel.svelte.ts";
  import { setAudioScene } from "./lib/stores/audio.svelte.ts";
  import { resetMm } from "./lib/stores/mm.svelte.ts";
  import CyberBackground from "./lib/components/CyberBackground.svelte";
  import RotateOverlay from "./lib/components/RotateOverlay.svelte";
  import { isPhone } from "./lib/device.svelte.ts";
  import { themeState } from "./lib/stores/theme.svelte.ts";
  import { settings } from "./lib/stores/settings.svelte.ts";
  import Title from "./lib/screens/Title.svelte";
  import NameScreen from "./lib/screens/NameScreen.svelte";
  import CharCreate from "./lib/screens/CharCreate.svelte";
  import ModeSelect from "./lib/screens/ModeSelect.svelte";
  import FreeDuelLobby from "./lib/screens/FreeDuelLobby.svelte";
  import World from "./lib/screens/World.svelte";
  import Duel from "./lib/screens/Duel.svelte";
  import DeckBuilder from "./lib/screens/DeckBuilder.svelte";
  import Leaderboard from "./lib/screens/Leaderboard.svelte";
  import Settings from "./lib/screens/Settings.svelte";
  import ModEditor from "./lib/screens/ModEditor.svelte";
  import WorldLoader from "./lib/components/WorldLoader.svelte";
  import { QueryClient } from "@tanstack/svelte-query";
  import { PersistQueryClientProvider } from "@tanstack/svelte-query-persist-client";
  import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

  // Card data is fetched on demand and cached for 24h (in memory + persisted to
  // localStorage), so the deck editor only fetches the pages it actually shows.
  const DAY = 1000 * 60 * 60 * 24;
  const queryClient = new QueryClient({
    defaultOptions: { queries: { staleTime: DAY, gcTime: DAY, retry: 1, refetchOnWindowFocus: false } },
  });
  const persister = createSyncStoragePersister({ storage: window.localStorage, key: "df.cards" });

  // Dev mocks (?worldmock / ?duelmock / ?charmock) run the client fully OFFLINE:
  // they fake a login and render from local assets, with the world socket left
  // anonymous/inert. So the server's sign-in rejection, e.g. the beta
  // public-access gate / kill switch firing SERVER_CLOSED at the anonymous socket
  //, must NOT bounce us back to the title. Read straight from the URL so this is
  // race-free against the async mock import below. Stripped from prod (DEV only).
  const DEV_MOCK =
    import.meta.env.DEV &&
    typeof location !== "undefined" &&
    (["worldmock", "duelmock", "charmock", "deckmock", "namemock", "modemock"].some(
      (k) => new URLSearchParams(location.search).get(k) === "1",
    ) ||
      new URLSearchParams(location.search).get("duelmock") === "showcase");

  // The world server pushes LoginOk whenever a socket authenticates from its
  // session cookie (fresh sign-in OR a returning visit). Update session data
  // globally; only navigate from the pre-auth screens so a mid-game reconnect
  // refreshes wallet/avatar without yanking the player out of the world.
  listen("LoginOk", (m) => {
    session.loginOk = m;
    session.authError = null;
    // Only navigate from the pre-auth screens (a mid-game reconnect must not yank the player out).
    // New players pick an in-game name first; everyone else lands straight in the world.
    if (session.route === "title" || session.route === "connecting") {
      session.route = loginRoute(m);
    }
  });

  // Name chosen on the onboarding screen → create a first character if needed, then choose a mode.
  listen("NameSet", (m) => {
    if (session.loginOk) {
      session.loginOk.avatar.name = m.name;
      session.loginOk.needsName = false;
      session.route = loginRoute(session.loginOk);
    }
  });

  // Sign-in refused, usually the beta kill switch (SERVER_CLOSED), which also force-disconnects
  // players already in-world (their reconnect lands here). Drop back to the title screen and show
  // the reason rather than letting the login silently time out.
  listen("LoginFail", (m) => {
    if (DEV_MOCK) return; // offline dev mock, ignore the server's sign-in rejection
    session.authError =
      m.message ??
      (m.reason === "INVALID_TOKEN"
        ? "Your session expired. Please sign in again."
        : "Sign-in failed. Please try again.");
    session.loginOk = null;
    session.route = "title";
  });

  // Daily login-streak reward arrives right after LoginOk (before World mounts), so
  // catch it globally: sync the wallet + stash the payout for World to celebrate.
  listen("DailyReward", (m) => {
    if (session.loginOk) session.loginOk.wallet = m.wallet;
    // On the gold-disk day the disk is already in LoginOk.ownedDuelDisks (granted before LoginOk),
    // but merge defensively so the Customizer unlocks it even if LoginOk raced.
    if (m.cosmetic?.kind === "dueldisk" && session.loginOk) {
      session.loginOk.ownedDuelDisks = [...new Set([...(session.loginOk.ownedDuelDisks ?? []), m.cosmetic.itemId])];
    }
    session.dailyReward = { streak: m.streak, dp: m.dp, cosmetic: m.cosmetic };
  });

  // One-time beta welcome DP grant, arrives right after LoginOk on a player's first login.
  // Sync the wallet and stash it for World to celebrate (shown ahead of the daily reward).
  listen("BetaWelcome", (m) => {
    if (session.loginOk) session.loginOk.wallet = m.wallet;
    session.betaWelcome = { dp: m.dp, shards: m.shards };
  });

  // Ranked standing refresh (after a rated game or an explicit RankGet): keep the
  // HUD/menu title in sync.
  listen("RankInfo", (m) => {
    // Only the WORLD ladder drives the HUD/menu title — other ladder refreshes
    // belong to the Free Duel lobby / leaderboard screens.
    if (m.format === "free" || (m.ladder && m.ladder !== "world")) return;
    if (session.loginOk) session.loginOk.ranking = m.ranking;
  });

  // A duel can begin from any screen (practice or an accepted invite), it is
  // muxed on the world socket, so listen globally and switch to the duel view.
  listen("DuelStart", (m) => {
    // Subscribe to the duel stream synchronously, the instant DuelStart lands, so the
    // server's immediate board/prompt burst is buffered into the store before the Duel
    // view mounts and reads it.
    beginDuel(m.seat, m.opponentName, getSocket(), m.opponentSleeve, m.opponentAvatar, m.opponentNpc, m.opponentRank, m.ranked, m.format, m.bestOf, m.gameNo, m.matchScore);
    resetMm(); // clear any matchmaking-search overlay state now the duel is starting
    session.route = "duel";
  });


  // Bo3 match: the server fires SideStart between games (after game 1/2). We stash the deck + the
  // 90s deadline on the duel store so the in-duel SideOverlay opens. A subsequent DuelStart for the
  // next game clears it (handled inside beginDuel).
  listen("SideStart", (m) => {
    duel.sidePhase = { gameNo: m.gameNo, score: m.score, deadlineMs: m.deadlineMs, deck: m.deck, pickTurn: m.pickTurn };
  });

  // Bo3 match concluded: stash the final result so the duel screen can show a match-end card.
  listen("MatchEnd", (m) => {
    duel.matchEnd = { winner: m.winner, finalScore: m.finalScore, youWon: m.youWon, rankResult: m.rankResult };
    // World ranked only: a Free Duel ranked result lives on the separate Free Duel ladder (see duel store).
    if (m.rankResult && session.loginOk && duel.format === "world") session.loginOk.ranking = m.rankResult.info;
  });

  // Dev: `?duelmock=1` injects a sample board and jumps to the duel view (UI design without a live
  // game); `?duelmock=showcase` additionally auto-runs the animation showcase reel (§7.4 of the
  // duel-animation plan). Dynamic import so the mock never lands in the production bundle.
  if (typeof location !== "undefined" && ["1", "showcase"].includes(new URLSearchParams(location.search).get("duelmock") ?? "")) {
    import("./lib/dev/mockDuel.ts").then((m) => m.startMockDuel());
  }

  // Dev: `?replay=<url>` plays a served replay file straight through the real pump — the way to
  // reproduce a player-reported duel bug EXACTLY as it happened (headless preview can't drive the
  // native file picker). Dev-only via the dynamic import + DEV gate.
  if (import.meta.env.DEV && typeof location !== "undefined") {
    const replayUrl = new URLSearchParams(location.search).get("replay");
    if (replayUrl) {
      void (async () => {
        const buf = new Uint8Array(await (await fetch(replayUrl)).arrayBuffer());
        const { readReplayFile } = await import("./lib/duel/replay.ts");
        const file = await readReplayFile(new File([buf], "repro.evorep"));
        const { startReplay } = await import("./lib/stores/duel.svelte.ts");
        const { go } = await import("./lib/stores/session.svelte.ts");
        startReplay(file, "mode");
        go("duel");
      })();
    }
  }

  // Dev: `?worldmock=1` fakes a login and drops into the overworld (optionally `&map=map_a01`),
  // for designing the world view + NPCs without a Discord session. Dynamic import → dev-only.
  if (typeof location !== "undefined" && new URLSearchParams(location.search).get("worldmock") === "1") {
    import("./lib/dev/mockWorld.ts").then((m) => m.startMockWorld());
  }

  // Dev: `?modemock=1` fakes a login and lands on the Mode Select gateway (World vs Free Duel),
  // for designing the mode picker + Free Duel lobby offline. Dynamic import → dev-only.
  if (typeof location !== "undefined" && new URLSearchParams(location.search).get("modemock") === "1") {
    import("./lib/dev/mockWorld.ts").then((m) => m.startMockMode());
  }

  // Dev: `?deckmock=1` fakes a login, seeds a collection + starter deck, and opens the
  // Deck Editor directly, for designing the editor (drag-and-drop, zones) offline.
  if (typeof location !== "undefined" && new URLSearchParams(location.search).get("deckmock") === "1") {
    import("./lib/dev/mockDeck.ts").then((m) => m.startMockDeck());
  }

  // Dev: `?charmock=1` fakes a login and opens the duelist customizer directly (no flaky world
  // round-trip), for designing the avatar + sleeve catalog. Add `&starter=1` for the required
  // first-run route with only the default cosmetics. Dynamic import → dev-only.
  if (typeof location !== "undefined" && new URLSearchParams(location.search).get("charmock") === "1") {
    import("./lib/dev/mockWorld.ts").then((m) => {
      if (new URLSearchParams(location.search).get("starter") === "1") m.startMockCharacter();
      else { m.startMockWorld(); session.customizing = true; }
    });
  }

  // Dev: `?namemock=1` fakes a login that needs a name and renders the onboarding name screen,
  // for designing it offline. Dynamic import → dev-only.
  if (typeof location !== "undefined" && new URLSearchParams(location.search).get("namemock") === "1") {
    import("./lib/dev/mockName.ts").then((m) => m.startMockName());
  }

  // Dev: `?mapedit=1` opens the scene-map authoring tool (paint collision, drop
  // doors/props, export map.json). No login needed; `&map=<id>` edits an existing
  // scene map. The component is dynamic-imported at the route so it stays dev-only.
  if (typeof location !== "undefined" && new URLSearchParams(location.search).get("mapedit") === "1") {
    session.route = "mapedit";
  }

  // Booted into "connecting" because we have a stored token (see session store): we're
  // waiting for the socket's LoginOk. LoginFail already drops us to the login screen; this
  // covers the silent cases (server unreachable, no reply) so we don't hang on the loader.
  if (session.route === "connecting") {
    setTimeout(() => {
      if (session.route === "connecting") {
        session.authError = "Couldn't reach the server. Please sign in again.";
        session.route = "title";
      }
    }, 8000);
  }

  // The connecting screen reuses the world's map loader (WorldLoader) so "entering"
  // and "loading a map" look identical. There's no real progress signal while we wait
  // for LoginOk, so creep the ring asymptotically toward ~0.92; LoginOk navigates away
  // (unmounting the loader) before it would ever complete.
  let connectProgress = $state(0.04);
  $effect(() => {
    if (session.route !== "connecting") return;
    const id = setInterval(() => {
      connectProgress = Math.min(0.92, connectProgress + (0.92 - connectProgress) * 0.08);
    }, 120);
    return () => clearInterval(id);
  });

  // The Three.js cyber world backs the menu screens AND the duel arena (the LINK VRAINS
  // cyberspace the duel happens inside). Only the in-world view draws its own canvas, so we
  // drop the shared one there to save the GPU.
  // …unless the active pack replaces the backdrop, in which case we skip the WebGL world entirely (one
  // fewer GPU context) and show their flat field via a CSS layer. Two INDEPENDENT backdrops: the DUEL
  // background (behind duels + the live editor preview) and the MENU/LOGIN background (every other
  // non-world screen) — so a pack can change one, both, or neither (each falls back to the cyber world).
  const duelCtx = $derived(session.route === "duel" || session.route === "modeditor");
  const themeBg = $derived(duelCtx ? themeState.hasDuelBg : themeState.hasMenuBg);
  const bgVar = $derived(duelCtx ? "--df-bg-field" : "--df-bg-menu");
  const showCyber = $derived(session.route !== "world" && !themeBg && settings.animatedBackdrop && !settings.performanceMode);
  const showThemeField = $derived(session.route !== "world" && themeBg);
  // Dense work surfaces (long reading/scanning tasks): keep the cyber world but
  // pull it far back behind a void scrim so content carries the contrast.
  const dimCyber = $derived(session.route === "deck" || session.route === "settings" || session.route === "leaderboard");
  // UI-first routes use a 1720×720 logical canvas on a 3440×1440 ultrawide,
  // then present it at 2×. Gameplay renderers keep the physical viewport and
  // own their camera/HUD scaling separately.
  const scaleRouteUi = $derived(
    session.route !== "world" &&
      session.route !== "duel" &&
      session.route !== "connecting" &&
      session.route !== "character" &&
      session.route !== "deck" &&
      session.route !== "modeditor" &&
      session.route !== "mapedit",
  );

  // Player-supplied background audio follows the screen: their overworld queue in the world, their
  // duel queue in a duel, silence elsewhere. No audio uploaded → stays silent (the default).
  $effect(() => {
    setAudioScene(session.route === "duel" ? "duel" : session.route === "world" ? "overworld" : null);
  });

  // Suppress the browser's native right-click menu over the game (it has nothing to do with the
  // game and just covers the board/world). Kept on text fields so right-click paste still works
  // in chat / the card search. Then make right-click double as "back / cancel": replay Escape on
  // the document so every screen's existing Esc handling runs (close the open menu → pile view →
  // cancel a cancelable target pick → close a dialog; in the empty world it toggles the menu).
  function onContextMenu(e: MouseEvent) {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
    e.preventDefault();
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }));
  }
</script>

<svelte:window oncontextmenu={onContextMenu} />

<PersistQueryClientProvider client={queryClient} persistOptions={{ persister, maxAge: DAY }}>
  {#if showThemeField}
    <div class="fixed inset-0 -z-10" style="background: var({bgVar}, var(--color-void))"></div>
  {/if}
  <!-- modpack frame: an ornate border PNG. A BACKDROP layer (above the field bg, below ALL screen
       content), so it shows through the screen's transparent areas / edges but can never cover the UI.
       Even a fully-opaque image here is just a background — the controls that clear it stay visible. -->
  <div class="pointer-events-none fixed inset-0 -z-[5]" style="background-image: var(--df-frame, none); background-size: 100% 100%; background-repeat: no-repeat"></div>
  {#if showCyber}
    <CyberBackground />
    {#if dimCyber}
      <div class="pointer-events-none fixed inset-0 -z-[6] bg-void/60"></div>
    {/if}
  {/if}

  {#key session.route}
    <div class="screen" data-route={session.route}>
      <div class="route-shell" class:scale-for-ultrawide={scaleRouteUi}>
        {#if session.route === "connecting"}
          <WorldLoader progress={connectProgress} phase="Establishing link" />
        {:else if session.route === "title"}
          <Title />
        {:else if session.route === "name"}
          <NameScreen />
        {:else if session.route === "character"}
          <CharCreate required onClose={() => (session.route = "mode")} />
        {:else if session.route === "mode"}
          <ModeSelect />
        {:else if session.route === "freeduel"}
          <FreeDuelLobby />
        {:else if session.route === "deck"}
          <DeckBuilder />
        {:else if session.route === "world"}
          <World />
        {:else if session.route === "duel"}
          <Duel />
        {:else if session.route === "leaderboard"}
          <Leaderboard />
        {:else if session.route === "settings"}
          <Settings />
        {:else if session.route === "modeditor"}
          <ModEditor />
        {:else if session.route === "mapedit"}
          {#await import("./lib/dev/MapEditor.svelte") then M}
            <M.default />
          {/await}
        {/if}
      </div>
    </div>
  {/key}

  {#if isPhone()}
    <RotateOverlay />
  {/if}

</PersistQueryClientProvider>

<style>
  .screen {
    position: fixed;
    inset: 0;
    /* HUD power-on when the route changes ({#key} remounts this element). */
    animation: screen-in 0.34s var(--ease-out-quint);
  }
  .route-shell {
    position: absolute;
    inset: 0;
  }
  @media (min-width: 1600px) and (min-height: 720px) and (min-aspect-ratio: 11/5) {
    .route-shell.scale-for-ultrawide {
      right: auto;
      bottom: auto;
      width: 50%;
      height: 50%;
      transform: scale(2);
      transform-origin: top left;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .screen {
      animation: none;
    }
  }
</style>
